import type {
  ComparableCoolingObservationValue,
  ComparableDeliveryEventValue,
  ComparableEvidenceAuthority,
  ComparableObservation,
  ComparableProjectRecord,
  ComparablePowerObservationValue,
  ComparablePowerMwBasis,
  ComparableWaterObservationValue,
  DataCenterOperatingModel,
  DataCenterWorkload,
  EconomicEvidenceBasis,
  EconomicSourceRef,
  PowerMwBasis,
} from './economicContribution';

export const ECONOMIC_RESEARCH_SCHEMA_VERSION = 1 as const;
export const ECONOMIC_RESEARCH_SOURCE_REGISTRY_VERSION = 'economic-research-sources-v2' as const;
export const ECONOMIC_RESEARCH_RUN_POLICY_VERSION = 'economic-research-run-policy-v2' as const;

export const ECONOMIC_RESEARCH_FACETS = Object.freeze(
  ['jobs', 'water', 'cooling', 'power', 'delivery'] as const,
);
export type EconomicResearchFacet = (typeof ECONOMIC_RESEARCH_FACETS)[number];

export type EconomicResearchObjectiveKind =
  | 'discover-comparable-cohort'
  | 'enrich-comparable-project'
  | 'refresh-comparable-project';

export interface EconomicResearchObjective {
  kind: EconomicResearchObjectiveKind;
  requestedFacets: readonly EconomicResearchFacet[];
  /** Required for enrich/refresh; omitted for cohort discovery. */
  projectId?: string;
}

export const DEFAULT_ECONOMIC_RESEARCH_OBJECTIVE: EconomicResearchObjective = Object.freeze({
  kind: 'discover-comparable-cohort',
  requestedFacets: ECONOMIC_RESEARCH_FACETS,
});

export interface EconomicResearchFacetCoverageEntry {
  requested: boolean;
  status:
    | 'not-requested'
    | 'not-started'
    | 'evidence-found'
    | 'no-policy-valid-evidence'
    | 'needs-context'
    | 'failed';
  policyValidObservationCount: number;
  heldObservationCount: number;
  excludedObservationCount: number;
  sourceIds: readonly string[];
  limitations: readonly string[];
}

/** All facets are present so absent evidence cannot be mistaken for unrequested work. */
export type EconomicResearchFacetCoverage = Readonly<
  Record<EconomicResearchFacet, EconomicResearchFacetCoverageEntry>
>;

export type EconomicResearchStage =
  | 'queued'
  | 'discovering'
  | 'retrieving'
  | 'extracting'
  | 'validating'
  | 'complete';

export type EconomicResearchTerminalResult =
  | 'complete-with-evidence'
  | 'complete-no-policy-valid-evidence'
  | 'complete-needs-context'
  | 'failed';

export type EconomicResearchSourceTier = 'A' | 'B' | 'C' | 'D';
export type EconomicResearchAdapterKind =
  | 'cec-project-docket'
  | 'socrata-exact-row'
  | 'texas-chapter-313-detail'
  | 'official-document'
  | 'assessor-tax';

export type EconomicResearchObservationType =
  | 'project-identity'
  | 'power-mw'
  | 'permanent-onsite-jobs'
  | 'jobs-created-to-date'
  | 'jobs-committed'
  | 'construction-peak-workers'
  | 'construction-average-workers'
  | 'construction-job-years'
  | 'capital-investment-usd'
  | 'annual-payroll-usd'
  | 'assessed-value-usd'
  | 'property-tax-paid-usd'
  | 'pilot-payment-usd'
  | 'water-design-estimate'
  | 'water-application-request'
  | 'water-permit-limit'
  | 'water-reported-actual'
  | 'cooling-configuration'
  | 'power-configuration'
  | 'delivery-event';

export interface EconomicResearchSourceDefinition {
  schemaVersion: typeof ECONOMIC_RESEARCH_SCHEMA_VERSION;
  id: string;
  label: string;
  jurisdictionCoverage: readonly string[];
  officialDomains: readonly string[];
  tier: EconomicResearchSourceTier;
  adapterKind: EconomicResearchAdapterKind;
  adapterSchemaVersion: string;
  parserContractVersion: string;
  supportedObservationTypes: readonly EconomicResearchObservationType[];
  queryTemplate: string;
  refreshCadenceHours: number;
  termsAndRateLimits: string;
  requiresJavaScript: boolean;
  requiresOcr: boolean;
  fixtureArtifactHash: string;
}

export interface EconomicResearchSiteBrief {
  schemaVersion: typeof ECONOMIC_RESEARCH_SCHEMA_VERSION;
  siteId: string;
  siteName: string;
  address?: string;
  location: {
    lat: number;
    lng: number;
    stateFips: string;
    countyFips: string;
    stateCode: string;
    countyName?: string;
  };
  /** Empty when load is not yet known; comparable discovery must still proceed. */
  load: {
    exactMw?: number;
    lowMw?: number;
    highMw?: number;
    basis?: PowerMwBasis;
    phaseBasis?: string;
  };
  operatingModel: DataCenterOperatingModel;
  workload: DataCenterWorkload;
  existingSourceUrls: readonly string[];
}

export interface EconomicResearchRunCounts {
  sourcesAttempted: number;
  candidatesFound: number;
  recordsPromoted: number;
  /**
   * Promoted records that actually added or changed registry evidence.
   * Optional only for runs persisted before the evidence-change counter was
   * introduced; an absent value must never be interpreted as new evidence.
   */
  recordsChanged?: number;
  recordsHeld: number;
  recordsExcluded: number;
  cacheHits: number;
}

export interface EconomicResearchCostCounters {
  retrievalBytes: number;
  modelInputTokens: number;
  modelOutputTokens: number;
  modelCalls: number;
}

export interface EconomicResearchArtifact {
  schemaVersion: typeof ECONOMIC_RESEARCH_SCHEMA_VERSION;
  artifactId: string;
  sourceDefinitionId: string;
  canonicalUrl: string;
  officialDomain: string;
  contentType: string;
  retrievedAt: string;
  storagePath: string;
  /** Immutable GCS object generation returned by the create-only write. */
  storageGeneration?: string;
  sha256: string;
  extractedTextPath?: string;
  extractionSha256?: string;
  retrievalStatus: 'retrieved' | 'not-modified' | 'rejected' | 'failed';
  byteLength: number;
  parserContractVersion: string;
  failureCode?: string;
}

export interface EconomicResearchClaimValue {
  value?: number | string;
  low?: number;
  high?: number;
}

export type EconomicResearchStructuredClaimValue =
  | ComparableWaterObservationValue
  | ComparableCoolingObservationValue
  | ComparablePowerObservationValue
  | ComparableDeliveryEventValue;

export interface EconomicResearchClaim {
  schemaVersion: typeof ECONOMIC_RESEARCH_SCHEMA_VERSION;
  claimId: string;
  runId: string;
  artifactId: string;
  sourceDefinitionId: string;
  observationType: EconomicResearchObservationType;
  value: EconomicResearchClaimValue;
  units: string;
  evidenceBasis: Exclude<EconomicEvidenceBasis, 'modelled' | 'operator-provided'>;
  /** Optional only on legacy claims. Structured claims require authority before promotion. */
  evidenceAuthority?: ComparableEvidenceAuthority;
  /** Discriminated value for water/cooling/power/delivery claims. */
  structuredValue?: EconomicResearchStructuredClaimValue;
  excerpt: string;
  locator: string;
  sourceFieldPaths: readonly string[];
  entityName: string;
  countyFips?: string;
  phaseBasis: string;
  mwBasis?: ComparablePowerMwBasis;
  confidenceFlags: readonly string[];
  parserContractVersion: string;
  validatedAgainstArtifact: boolean;
}

export interface EconomicResearchLead {
  schemaVersion: typeof ECONOMIC_RESEARCH_SCHEMA_VERSION;
  leadId: string;
  runId: string;
  sourceDefinitionId: string;
  candidateId: string;
  status: 'held' | 'excluded' | 'promoted';
  artifactIds: readonly string[];
  claimIds: readonly string[];
  validationReasons: readonly string[];
  exclusionReasons: readonly string[];
  approvedProjectId?: string;
  /** Normalized candidate retained server-side; never included in the run view. */
  proposedRecord?: ComparableProjectRecord;
  createdAt: string;
  updatedAt: string;
}

export interface FiscalBaselineDraft {
  schemaVersion: typeof ECONOMIC_RESEARCH_SCHEMA_VERSION;
  draftId: string;
  runId: string;
  jurisdictionLabel: string;
  taxYear?: number;
  parcelId?: string;
  assessedValueUsd?: number;
  propertyTaxPaidUsd?: number;
  publishedRateComponents: readonly {
    label: string;
    rateDecimal: number;
    sourceClaimId: string;
  }[];
  unresolvedAssumptions: readonly string[];
  artifactIds: readonly string[];
  status: 'observed-facts-only' | 'needs-context' | 'unavailable';
}

export interface EconomicResearchEvidenceStep {
  kind: 'official-record' | 'extracted-claim' | 'compatibility-decision';
  label: string;
  detail: string;
  sourceUrl?: string;
  excerpt?: string;
  locator?: string;
}

export interface EconomicResearchComparableSummary {
  projectId: string;
  facilityName: string;
  locality?: string;
  stateCode: string;
  score: number;
  mw?: {
    value: number;
    basis: ComparablePowerMwBasis;
    phaseBasis: string;
    /** Exact source association for this scalar; never inferred from the project-wide trail. */
    sources: readonly EconomicSourceRef[];
  };
  permanentJobs?: {
    low: number;
    high: number;
    evidenceBasis: Exclude<EconomicEvidenceBasis, 'modelled' | 'operator-provided'>;
    phaseBasis: string;
    /** Exact source association for this scalar; it may differ from the MW source. */
    sources: readonly EconomicSourceRef[];
  };
  reasons: readonly string[];
  incompatibilities: readonly string[];
  compatibleForJobsPerMw: boolean;
  evidenceTrail: readonly EconomicResearchEvidenceStep[];
  waterTimeline?: readonly ComparableObservation<ComparableWaterObservationValue>[];
  coolingObservations?: readonly ComparableObservation<ComparableCoolingObservationValue>[];
  powerObservations?: readonly ComparableObservation<ComparablePowerObservationValue>[];
  deliveryEvents?: readonly ComparableObservation<ComparableDeliveryEventValue>[];
}

export interface EconomicResearchHeldSummary {
  leadId: string;
  sourceLabel: string;
  candidateLabel: string;
  reasons: readonly string[];
  evidenceTrail: readonly EconomicResearchEvidenceStep[];
}

/** A configured source that could not be checked during an otherwise usable run. */
export type EconomicResearchSourceFailureKind = 'schema-drift' | 'availability' | 'other';

export interface EconomicResearchSourceFailure {
  sourceId: string;
  sourceClass: string;
  /** Optional only for result snapshots created before failure classification. */
  failureKind?: EconomicResearchSourceFailureKind;
}

/**
 * A cohort-level observation derived only from topic-compatible, approved
 * comparable projects. Numeric and categorical cells are withheld until at
 * least three distinct projects support the exact same evidence scope.
 */
export interface EconomicResearchComparableNumericAggregate {
  key: string;
  recordCount: number;
  low: number;
  median: number;
  high: number;
  units: string;
}

export interface EconomicResearchComparableCategoricalAggregate {
  key: string;
  recordCount: number;
}

export interface EconomicResearchComparableAggregate {
  topic: EconomicResearchFacet;
  compatibleRecordCount: number;
  method: 'topic-compatible-envelope' | 'topic-compatible-frequency' | null;
  numeric: readonly EconomicResearchComparableNumericAggregate[];
  categorical: readonly EconomicResearchComparableCategoricalAggregate[];
}

export interface EconomicResearchResultSnapshot {
  schemaVersion: typeof ECONOMIC_RESEARCH_SCHEMA_VERSION;
  snapshotId: string;
  siteFingerprint: string;
  generatedAt: string;
  /** Optional only for snapshots created before objective-aware research. */
  objective?: EconomicResearchObjective;
  facetCoverage?: EconomicResearchFacetCoverage;
  policyValidRecords: readonly EconomicResearchComparableSummary[];
  /** Optional for snapshots created before cohort aggregates were published. */
  comparableAggregates?: readonly EconomicResearchComparableAggregate[];
  held: readonly EconomicResearchHeldSummary[];
  excluded: readonly EconomicResearchHeldSummary[];
  /** Present when only part of the configured source set completed successfully. */
  sourceFailures?: readonly EconomicResearchSourceFailure[];
  basisCounts: Partial<Record<Exclude<EconomicEvidenceBasis, 'modelled' | 'operator-provided'>, number>>;
  fiscalBaselineDrafts: readonly FiscalBaselineDraft[];
}

/**
 * Compact server-owned pointer published on the canonical site when a usable
 * research run completes. The full immutable result stays on
 * `economicResearchRuns/{runId}`; the pointer is small enough for the site
 * document and gives SED freshness checks a deterministic revision identity.
 *
 * Held/excluded leads are deliberately absent. They remain research audit
 * records and can never cross this boundary as report facts.
 */
export interface EconomicResearchSitePointer {
  schemaVersion: typeof ECONOMIC_RESEARCH_SCHEMA_VERSION;
  runId: string;
  resultSnapshotId: string;
  siteFingerprint: string;
  generatedAt: string;
  policyVersion: string;
  sourceDefinitionVersion: string;
}

export interface EconomicResearchRun {
  schemaVersion: typeof ECONOMIC_RESEARCH_SCHEMA_VERSION;
  runId: string;
  requesterUid: string;
  siteOwnerUid: string;
  siteId: string;
  requestHash: string;
  siteFingerprint: string;
  /** Server-only request facts used to revalidate the canonical site before publication. */
  siteBrief: EconomicResearchSiteBrief;
  /** Optional only for persisted v1 runs; every new run should resolve and store it. */
  objective?: EconomicResearchObjective;
  sourceDefinitionVersion: string;
  policyVersion: string;
  parserVersions: Readonly<Record<string, string>>;
  stage: EconomicResearchStage;
  terminalResult?: EconomicResearchTerminalResult;
  progress: number;
  progressSeq: number;
  currentStageLabel: string;
  currentSourceId?: string;
  sourceIds: readonly string[];
  sourcesAttempted: readonly string[];
  counts: EconomicResearchRunCounts;
  costs: EconomicResearchCostCounters;
  attempt: number;
  heartbeatAt?: string;
  leaseExpiresAt?: string;
  dispatchState: 'pending' | 'leased' | 'dispatched' | 'not-required';
  dispatchLeaseId?: string;
  dispatchLeaseExpiresAt?: string;
  dispatchRetryAt?: string;
  executionName?: string;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  finishedAt?: string;
  staleAt: string;
  errorCode?: string;
  failedSourceClass?: string;
  errorMessage?: string;
  resultSnapshot?: EconomicResearchResultSnapshot;
}

/** Sanitized API projection. Raw rows, artifact paths and model inputs never leave the server. */
export interface EconomicResearchRunView {
  schemaVersion: typeof ECONOMIC_RESEARCH_SCHEMA_VERSION;
  runId: string;
  siteId: string;
  siteFingerprint: string;
  objective?: EconomicResearchObjective;
  stage: EconomicResearchStage;
  terminalResult?: EconomicResearchTerminalResult;
  progress: number;
  currentStageLabel: string;
  currentSourceLabel?: string;
  sourcesAttempted: readonly string[];
  counts: EconomicResearchRunCounts;
  createdAt: string;
  updatedAt: string;
  finishedAt?: string;
  staleAt: string;
  failedSourceClass?: string;
  retryable: boolean;
  resultSnapshot?: EconomicResearchResultSnapshot;
}

export interface CreateEconomicResearchRunRequest {
  siteId: string;
  /** Lookup hint only. The server resolves and authorizes the canonical owner. */
  ownerUid?: string;
  /** Server defaults to DEFAULT_ECONOMIC_RESEARCH_OBJECTIVE when omitted. */
  objective?: EconomicResearchObjective;
}

export interface CreateEconomicResearchRunResponse {
  run: EconomicResearchRunView;
  duplicate: boolean;
}
